import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import isEqualWith from 'lodash/isEqualWith';

import { useSendInvestorCompanyMutation } from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { texts } from 'utils/languages/fr';
import { isStatusOk } from 'utils/helpers';
import { useTunnelChangeHandlers } from 'utils/hooks';

import { TunnelStep, TunnelBox, DocumentUpload } from 'containers/tunnel/common';
import { SubmitButton, RadioLabel } from 'components';
import { Box, Typography, RadioGroup } from '@mui/material';

const containerSx = { display: 'flex', flexWrap: 'wrap', flexDirection: 'row' };
const labelSx = { width: '100%', fontSize: '16px', color: 'common.black', marginBottom: '20px' };
const optionSx = { mb: '20px' };

const VigilanceInvestor = props => {
    const { handleNextStep, userStep } = props;

    const [sendInvestorCompany, { isLoading: buttonLoader, isSuccess }] =
        useSendInvestorCompanyMutation();
    const setTunnelChangesHandlers = useTunnelChangeHandlers();

    const internalUser = useSelector(userSelectors.getInternalUser);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const isResubmit = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);
    const isIndirect = useMemo(
        () => tunnelSubscription?.indirectUntransferred,
        [tunnelSubscription]
    );

    const initialValues = useMemo(
        () => ({
            unlistedFintechCompany: internalUser?.investorCompany?.unlistedFintechCompany,
            listedCompany: internalUser?.investorCompany?.listedCompany,
            publicAuthority: internalUser?.investorCompany?.publicAuthority,
        }),
        [internalUser]
    );

    const initialFiles = useMemo(
        () => internalUser?.investorCompany?.investorReducedVigilanceDocuments || [],
        [internalUser]
    );

    const [reducedVigilance, setReducedVigilance] = useState(initialValues);
    const [selectedFiles, setSelectedFiles] = useState(initialFiles);

    const displayUpload = useMemo(
        () => Object.keys(reducedVigilance).some(value => reducedVigilance[value] === true),
        [reducedVigilance]
    );

    useEffect(() => {
        setReducedVigilance(initialValues);
    }, [initialValues]);

    const handleChange = ({ target: { name, value } }) => {
        setReducedVigilance(prevState => ({ ...prevState, [name]: !!value }));
    };

    const handleFileChange = ({ target: { files } }) => {
        const file = files[0];
        setSelectedFiles(prevState => [...prevState, file]);
    };

    const removeFileFromState = useCallback(
        id => setSelectedFiles(selectedFiles.filter((_, i) => i !== +id)),
        [selectedFiles]
    );

    const removeInvestorFile = useCallback(
        ({ currentTarget: { id } }) => {
            removeFileFromState(id);
        },
        [removeFileFromState]
    );

    useEffect(() => {
        if (isResubmit && isSuccess) {
            const files = internalUser?.investorCompany?.investorReducedVigilanceDocuments || [];
            setSelectedFiles(files);
        }
    }, [internalUser, setSelectedFiles, isResubmit, isSuccess]);

    const handleSubmit = useCallback(
        afterSubmitCallback => () => {
            let formData = new FormData();
            let filesToDelete = [];
            selectedFiles.forEach(file => formData.append('files[]', file));
            formData.append('body', JSON.stringify({ investorCompany: reducedVigilance }));

            // check options
            const allOptionsFalse = Object.keys(reducedVigilance).some(
                value => reducedVigilance[value] === true
            );
            if (!allOptionsFalse) {
                filesToDelete = selectedFiles.filter(file => !!file.id).map(file => file.id);
            }

            sendInvestorCompany({
                payload: formData,
                filesToDelete,
                callback: afterSubmitCallback,
            });
        },
        [selectedFiles, reducedVigilance, sendInvestorCompany]
    );

    useEffect(() => {
        if (isSuccess) {
            handleNextStep(true);
        }
    }, [handleNextStep, isSuccess]);

    const checkChanges = useCallback(() => {
        return (
            !isEqual(initialValues, reducedVigilance) ||
            !isEqualWith(initialFiles, selectedFiles, (objValue, othValue) => {
                return (
                    objValue.length === othValue.length &&
                    objValue.every((formObject, i) => isEqual(formObject, othValue[i]))
                );
            })
        );
    }, [initialValues, initialFiles, reducedVigilance, selectedFiles]);

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);

    useEffect(
        () => setTunnelChangesHandlers(handleSubmit, checkChanges, isResubmit),
        [handleSubmit, checkChanges, isResubmit, setTunnelChangesHandlers]
    );

    const { unlistedFintechCompany, listedCompany, publicAuthority } = reducedVigilance;

    const allowSubmit = useMemo(
        () =>
            isIndirect
                ? true
                : unlistedFintechCompany !== null &&
                  listedCompany !== null &&
                  publicAuthority !== null,
        [isIndirect, unlistedFintechCompany, listedCompany, publicAuthority]
    );

    return (
        <TunnelStep title={texts.VIGILANCE_INVESTOR}>
            <TunnelBox>
                <Box display="flex">
                    <RadioGroup sx={containerSx} onChange={handleChange}>
                        <Typography sx={labelSx} variant="body2" gutterBottom>
                            {texts.VIGILANCE_INVESTOR_LABEL_1}
                        </Typography>
                        <RadioLabel
                            sx={optionSx}
                            value={true}
                            label={texts.OUI}
                            checked={unlistedFintechCompany === true}
                            name="unlistedFintechCompany"
                        />
                        <RadioLabel
                            sx={optionSx}
                            label={texts.NON}
                            checked={unlistedFintechCompany === false}
                            name="unlistedFintechCompany"
                        />
                    </RadioGroup>
                </Box>
                <Box display="flex">
                    <RadioGroup sx={containerSx} onChange={handleChange}>
                        <Typography sx={labelSx} variant="body2" gutterBottom>
                            {texts.VIGILANCE_INVESTOR_LABEL_2}
                        </Typography>
                        <RadioLabel
                            sx={optionSx}
                            value={true}
                            label={texts.OUI}
                            checked={listedCompany === true}
                            name="listedCompany"
                        />
                        <RadioLabel
                            sx={optionSx}
                            label={texts.NON}
                            checked={listedCompany === false}
                            name="listedCompany"
                        />
                    </RadioGroup>
                </Box>
                <Box display="flex">
                    <RadioGroup sx={containerSx} onChange={handleChange}>
                        <Typography sx={labelSx} variant="body2" gutterBottom>
                            {texts.VIGILANCE_INVESTOR_LABEL_3}
                        </Typography>
                        <RadioLabel
                            sx={optionSx}
                            value={true}
                            label={texts.OUI}
                            checked={publicAuthority === true}
                            name="publicAuthority"
                        />
                        <RadioLabel
                            sx={optionSx}
                            label={texts.NON}
                            checked={publicAuthority === false}
                            name="publicAuthority"
                        />
                    </RadioGroup>
                </Box>
                {displayUpload && (
                    <DocumentUpload
                        id="status-documents"
                        label={texts.STATUS_UPLOAD_FILE}
                        selectedFiles={selectedFiles}
                        handleFileChange={handleFileChange}
                        removeFile={removeInvestorFile}
                    />
                )}
            </TunnelBox>
            <SubmitButton
                text={isResubmit && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT}
                onClick={changesMade || !hasStatusOk ? handleSubmit() : handleNextStep}
                loading={buttonLoader}
                disabled={!allowSubmit || buttonLoader}
            />
        </TunnelStep>
    );
};

VigilanceInvestor.propTypes = {
    handleNextStep: PropTypes.func,
    userStep: PropTypes.object,
};

export default VigilanceInvestor;
